// Разбор человеческого адреса рельса "/<продукт>/<раздел>[/<хвост>]" (T-286 §3/§4) — вынесено
// из useProductMenu.ts, чтобы обе функции были чистыми (без window, без React) и тестировались
// node:test без jsdom.
// Чистый разбор пути (без window). Сегменты декодируются (раздел/продукт могут прийти
// процентно-кодированными); slug/entryId/hasItem — двухсегментный фолбэк на случай, когда
// каталог продуктов ещё не загружен и resolveDoorEntry не может определить вход.
export function parseEntryFromPath(pathname) {
    const segments = pathname.split("/").filter(Boolean).map(decodeURIComponent);
    if (segments.length === 0)
        return null;
    const slug = segments[0];
    const item = segments.length > 1 ? segments[1] : undefined;
    return { slug, entryId: item ? `${slug}/${item}` : slug, hasItem: !!item, segments };
}
// stripKnownDoorEntrySegment — T-286 §4 правило 1: старые адреса из уже разосланных уведомлений
// несли id входа дважды (/crm/crm/deals, /personnel/hr/dismissals) — если второй сегмент вместе с
// первым составляет реальный id входа каталога, он выбрасывается как служебный остаток, не
// значимый раздел. Правило постоянное (закон адреса), не временная подпорка.
function stripKnownDoorEntrySegment(segments, products) {
    if (segments.length < 2)
        return segments;
    const candidate = `${segments[0]}/${segments[1]}`;
    if (products.some((p) => p.id === candidate))
        return [segments[0], ...segments.slice(2)];
    return segments;
}
// prefixOwnerSlug — T-286 §4 правило 2: первый сегмент сам по себе не слаг ни одного установленного
// продукта, но ровно у одного продукта есть вход "<его слаг>/" + первый сегмент (напр.
// /hr/dismissals/<ref> — владелец входа "personnel/hr" — personnel) — слаг владельца подставляется
// впереди. Больше одного совпадения — неоднозначно, адрес не трогается.
function prefixOwnerSlug(segments, products) {
    const installedSlugs = new Set(products.map((p) => p.product));
    if (segments.length === 0 || installedSlugs.has(segments[0]))
        return segments;
    const owners = new Set(products.filter((p) => p.id === `${p.product}/${segments[0]}`).map((p) => p.product));
    if (owners.size !== 1)
        return segments;
    const [owner] = owners;
    return [owner, ...segments];
}
// resolveDoorEntry — "раздел → вход" (T-286 §3): первый сегмент — слаг продукта, вход решает
// каталог по разделу (segments[1]), не по служебному id входа в адресе. Хвост ПОСЛЕ границы
// вход/раздел отдаётся продукту section'ом целиком, склеенным через "/" (карточка сотрудника
// внутри «Команды» — personnel/people/e17 → entryId "personnel", section "people/e17").
export function resolveDoorEntry(entry, products) {
    const segments = stripKnownDoorEntrySegment(prefixOwnerSlug(entry.segments, products), products);
    const slug = segments[0];
    const doors = products.filter((p) => p.product === slug);
    const tail = segments.length > 1 ? segments.slice(1).join("/") : null;
    // Каталог не готов или продукт неизвестен (denied/404 решает вызывающая сторона) — исходный
    // двухсегментный разбор без раздела, как раньше.
    if (doors.length === 0)
        return { entryId: entry.entryId, section: null };
    // Одна дверь — она и есть вход, остальное — раздел целиком (в т.ч. сегодняшний primary).
    if (doors.length === 1)
        return { entryId: doors[0].id, section: tail };
    // Несколько дверей — вход тот, чей набор разделов содержит segments[1].
    if (segments.length > 1) {
        const bySection = doors.find((d) => (d.sections ?? []).includes(segments[1]));
        if (bySection)
            return { entryId: bySection.id, section: tail };
    }
    // Совпадения нет (неизвестный раздел, прямая ссылка на карточку) — primary, иначе дверь с
    // наименьшим order.
    const primary = doors.find((d) => d.id === d.product)
        ?? doors.reduce((a, b) => ((b.order ?? 0) < (a.order ?? 0) ? b : a));
    return { entryId: primary.id, section: tail };
}
